<script lang="ts">
  import type { generateKey } from "@/libs/ui/svelte/key.svelte"
  import { fade } from "@/libs/ui/transition"
  import type { Snippet } from "svelte"
  import type { ClassValue, HTMLAttributes } from "svelte/elements"

  let {
    key,
    title,
    description,
    children,
    footer,
    titleClass,
    descriptionClass,
    footerClass,
    ...props
  }: {
    title: string
    titleClass?: ClassValue
    description?: string
    descriptionClass?: ClassValue
    footer?: Snippet
    footerClass?: ClassValue
    /** Supplementary view above the title and description */
    children?: Snippet
    key?: ReturnType<typeof generateKey>
  } & HTMLAttributes<HTMLElement> = $props()

  const fadeInOptions = { duration: 350, delay: 350 }
  const fadeOutOptions = { duration: 350 }
</script>

<section
  {...props}
  class={[
    "flex size-full flex-col items-center p-4",
    "liqa-lg:mx-auto liqa-lg:max-w-[42.5rem] liqa-lg:p-10",
    props.class,
  ]}
>
  {#key key?.value}
    <div
      class="my-auto flex flex-col items-center justify-center gap-6 liqa-lg:gap-10 [&:nth-child(2)]:absolute"
      in:fade={fadeInOptions}
      out:fade={fadeOutOptions}
    >
      {@render children?.()}

      <div class="flex flex-col gap-2 text-center">
        <h2 class={[titleClass, "text-xl tracking-tight"]}>{title}</h2>

        {#if description}
          <p
            class={[
              descriptionClass,
              "mx-auto max-w-[22ch] text-base font-normal -tracking-[0.02rem] text-opacity-60",
              "liqa-md:max-w-[30ch] liqa-lg:max-w-[35ch]",
            ]}
          >
            {description}
          </p>
        {/if}
      </div>
    </div>
  {/key}

  {#if footer}
    <div
      class={[
        "flex w-full flex-col items-center justify-center gap-2 liqa-lg:flex-row",
        footerClass,
      ]}
    >
      {@render footer()}
    </div>
  {/if}
</section>

<style lang="postcss">
  :where(p, h2) {
    color: rgb(var(--color) / var(--tw-text-opacity, 1));
  }
</style>
